import * as React from 'react';

import { useDeviceStore, selectIsMobile, selectIsTablet } from 'features/ui/store/useDeviceStore';

import './_cards.scss';

import {PriceListCard} from "./pricelistCard";
import {PurchaseObjectCard} from "./purchaseObjectCard";
import {PremisesTableCard} from "./premisesTableCard";
import {ErrandsCard} from "./errandsCard";
import {MainGrid} from "@atoms/Layout/MainGrid";
import {DisposalForm, ObjectStatus, ResidenceType, SortBy} from 'features/search/filters/enums';
import { ParkingCard } from './parkingCard';

interface Props {
    foldableCount?: number;
    isFolded?: boolean;
    cardType?: DataTableCardType;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic table row data
    data: any;
    currentItem?: string;
    currentObjectId?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic row click handler
    onRowClick?: (data: any) => (e: any) => any;

    sort?: SortBy;

    editing?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic row data
    onDeleteRow?: (data: any) => void;

    emptyMessage?: string;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic row data
    onEditUp?: (data: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic row data
    onEditDown?: (data: any) => void;
}

export enum DataTableCardType {
    PRICELIST = 'pricelist',
    PURCHASE = 'purchase',
    PREMISES = 'premises',
    ERRANDS = 'errands',
    PARKING_STOCK = 'parking-stock',
    PARKING_SPOT = 'parking-spot',
}

export const TableCards: React.FunctionComponent<Props> = ({
    foldableCount, isFolded,
    data, currentItem, currentObjectId, onRowClick,
    editing, onDeleteRow, emptyMessage, cardType,
    onEditUp, onEditDown, sort
}) => {
    const isMobile = useDeviceStore(selectIsMobile);
    const isTablet = useDeviceStore(selectIsTablet);

    const activeObjectIndex = 0;

    const isRowVisible  = (index: number) => {
        const foldCount = (isMobile && !isTablet)
            ? foldableCount
                ? foldableCount
                : 10
            : 6;

        if (isFolded && index >= foldCount && index >= activeObjectIndex){
            return false;
        }

        return true;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic table row data
    const shouldHaveActiveClass = (data: any) => {
        let active: boolean = false;

        if(currentItem && data.apartmentNumber) {
            active = currentItem.toString() === data.apartmentNumber.toString();
            return active;
        }

        if(currentObjectId && data.apartmentNumber) {
            active = currentObjectId.toString() === data.apartmentNumber.toString();
        }

        return active;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic table row data
    const getCardType = (rowData: any, index: number) => {
        if(!isRowVisible(index)) return;

        switch (cardType) {
            case DataTableCardType.PRICELIST:
                return <PriceListCard
                    key={index}
                    object={rowData}
                    active={shouldHaveActiveClass(rowData)}
                />;
            case DataTableCardType.PARKING_SPOT:
            case DataTableCardType.PARKING_STOCK:
                return <ParkingCard
                    key={index}
                    object={rowData}
                    onParkingProject={cardType === DataTableCardType.PARKING_STOCK}
                />;
            case DataTableCardType.PURCHASE:
                return <PurchaseObjectCard
                    key={index}
                    object={rowData}
                    noOfItems={data.length}
                    edit={editing}
                    onDeleteRow={onDeleteRow}
                    onOrderUp={onEditUp}
                    onOrderDown={onEditDown}
                />;
            case DataTableCardType.PREMISES:
                return <PremisesTableCard
                    key={index}
                    object={rowData}
                />;
            case DataTableCardType.ERRANDS:
                return <ErrandsCard
                    key={index}
                    errand={rowData}
                    onCardClick={onRowClick && onRowClick(rowData)}
                />;
            default:
                return <div key={index}>kort {index}</div>;
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic table row data
    const sortDate = (a: any, b: any) => {
        const aSold = a.status === ObjectStatus.SOLD;
        const bSold = b.status === ObjectStatus.SOLD;

        if (aSold !== bSold) return aSold ? 1 : -1;

        const diff = a.inhabitDate - b.inhabitDate;
        return sort === SortBy.ACCESS_ASC ? diff : -diff;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic table row data
    const getTotalPrice = (obj: any) => {
        let price = obj.price;
        if(obj.otherExpenses) {
            price += obj.otherExpenses;
        }
        if(obj.jmnoFellesgjeld
            && obj.residenceType === ResidenceType.APARTMENT
            && (obj.disposalForm === DisposalForm.OWNERSHIP || obj.disposalForm === DisposalForm.OWNERSHIP_APARTMENT)
        ) {
            price += obj.jmnoFellesgjeld;
        }

        return price;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic table row data
    const sortTotalPrice = (a: any, b: any, _key: string) => {
        const aValue = getTotalPrice(a);
        const bValue = getTotalPrice(b);
        const aSold = a.status === ObjectStatus.SOLD;
        const bSold = b.status === ObjectStatus.SOLD;

        if (aSold !== bSold) return aSold ? 1 : -1;

        const diff = aValue - bValue;
        return sort === SortBy.ACCESS_ASC ? diff : -diff;
    }

    const getSortedCardData = () => {
        if(sort) {
            console.log(sort);
            switch (sort) {
                case SortBy.ACCESS_ASC:
                case SortBy.ACCESS_DESC:
                    return data.sort(sortDate);
                case SortBy.PRICE_ASC:
                case SortBy.PRICE_DESC:
                    return data.sort(sortTotalPrice);
            }
        }

        return data;
    }

    return (
        <div className='grid-table__cards'>
            {data.length === 0
                ? <div className="grid-table__empty-message">
                    {emptyMessage && emptyMessage}
                </div>
                : <MainGrid>
                    {getSortedCardData().map((rowData, index) => (
                        <React.Fragment key={`row-${index}`}>
                            {getCardType(rowData, index)}
                        </React.Fragment>
                    ))}
                </MainGrid>
            }
        </div>
    )
};
